import { useState } from "react";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  Globe,
  Image,
  FileText,
  PenTool,
  Calendar,
  GraduationCap,
  Search,
  Library,
  Building,
  Brain,
  Key,
  ClipboardList,
  Settings,
  Plus,
  Filter,
  Download,
  RefreshCw,
  CheckCircle2,
  Clock,
  Eye,
  Edit,
  Trash2,
} from "lucide-react";

const placeholderModules = [
  {
    title: "Website CMS",
    icon: Globe,
    description: "Manage homepage, about sections, and public content",
  },
  {
    title: "Gallery",
    icon: Image,
    description: "Upload and organize images and videos",
  },
  {
    title: "Books (PDFs)",
    icon: FileText,
    description: "Manage digital book uploads and metadata",
  },
  {
    title: "Blog",
    icon: PenTool,
    description: "Create and publish blog posts",
  },
  {
    title: "Events",
    icon: Calendar,
    description: "Manage community events and registrations",
  },
  {
    title: "Scholars",
    icon: GraduationCap,
    description: "Approve and manage scholar applications",
  },
  {
    title: "Research Tools",
    icon: Search,
    description: "SodhSanchay, SodhSandarbh, Śabdasaṅgraha management",
  },
  {
    title: "Digital Museum",
    icon: Building,
    description: "Muni Jambuvijayaji Research Center exhibits and multimedia",
  },
  {
    title: "AI & Indexing",
    icon: Brain,
    description: "Search index and AI configuration",
  },
  {
    title: "Access Requests",
    icon: Key,
    description: "Manage content access approvals",
  },
  {
    title: "Logs",
    icon: ClipboardList,
    description: "System audit trails and activity logs",
  },
  {
    title: "Settings",
    icon: Settings,
    description: "System configuration and preferences",
  },
];

const sampleItems: Record<
  string,
  Array<{
    id: string;
    name: string;
    category: string;
    date: string;
    status: string;
  }>
> = {
  Gallery: [
    {
      id: "GAL-01",
      name: "Jambuvijayji Manuscript Exhibition Photo 1",
      category: "Exhibition",
      date: "2026-08-01",
      status: "Published",
    },
    {
      id: "GAL-02",
      name: "Shantigram Center Inauguration",
      category: "Events",
      date: "2026-07-28",
      status: "Published",
    },
    {
      id: "GAL-03",
      name: "Rare Palm-Leaf Manuscript Digitization",
      category: "Manuscripts",
      date: "2026-07-15",
      status: "Draft",
    },
  ],
  "Books (PDFs)": [
    {
      id: "BK-101",
      name: "Tattvartha Sutra Commentary",
      category: "Philosophy",
      date: "2026-08-05",
      status: "Indexed",
    },
    {
      id: "BK-102",
      name: "Kalpasutra Rare Edition Vol 1",
      category: "Agam",
      date: "2026-07-29",
      status: "Indexed",
    },
    {
      id: "BK-103",
      name: "Prakrit Grammar & Lexicon",
      category: "Language",
      date: "2026-07-12",
      status: "Processing",
    },
  ],
  Events: [
    {
      id: "EV-501",
      name: "Annual Jain Manuscriptology Symposium",
      category: "Conference",
      date: "2026-09-10",
      status: "Upcoming",
    },
    {
      id: "EV-502",
      name: "Prakrit Script Workshop for Researchers",
      category: "Workshop",
      date: "2026-08-20",
      status: "Registration Open",
    },
  ],
  Scholars: [
    {
      id: "SCH-01",
      name: "Dr. Arvind Shah (SOAS London)",
      category: "Indology",
      date: "2026-08-02",
      status: "Verified",
    },
    {
      id: "SCH-02",
      name: "Prof. Sunita Jain (L.D. Institute)",
      category: "Manuscripts",
      date: "2026-07-30",
      status: "Pending Review",
    },
  ],
};

export function AdminPlaceholder({ title }: { title: string }) {
  const module =
    placeholderModules.find(m => m.title === title) || placeholderModules[0];
  const Icon = module.icon;
  const items = sampleItems[title] || [
    {
      id: `${title.slice(0, 3).toUpperCase()}-01`,
      name: `${title} Item #1`,
      category: "General",
      date: "2026-08-06",
      status: "Active",
    },
    {
      id: `${title.slice(0, 3).toUpperCase()}-02`,
      name: `${title} Item #2`,
      category: "General",
      date: "2026-08-04",
      status: "Active",
    },
    {
      id: `${title.slice(0, 3).toUpperCase()}-03`,
      name: `${title} Item #3`,
      category: "Archived",
      date: "2026-07-20",
      status: "Pending",
    },
  ];

  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = items.filter(
    item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title={title}>
      <div className="space-y-6">
        {/* Module Header Banner */}
        <div className="flex flex-col justify-between gap-4 rounded-2xl bg-gradient-to-r from-stone-900 via-stone-800 to-amber-950 p-6 text-white shadow-xl sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-amber-500/30 bg-amber-500/20 text-amber-400">
              <Icon className="h-7 w-7" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-amber-100">
                {title} Management
              </h1>
              <p className="mt-0.5 text-sm text-amber-200/70">
                {module.description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-xl border-amber-800/40 text-amber-200 hover:bg-amber-900/40"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Sync
            </Button>
            <Button className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 font-semibold text-stone-950 shadow-md hover:from-amber-600 hover:to-amber-700">
              <Plus className="mr-2 h-4 w-4" /> Add New
            </Button>
          </div>
        </div>

        {/* Toolbar & Filter Card */}
        <Card className="shadow-xs rounded-2xl border border-stone-200/80">
          <CardContent className="flex flex-col items-center justify-between gap-4 p-4 sm:flex-row">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder={`Search ${title.toLowerCase()}...`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="rounded-xl border-stone-200 bg-stone-50/50 pl-9 focus:bg-white"
              />
            </div>
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <Button
                variant="outline"
                className="rounded-xl border-stone-200 text-xs text-stone-700"
              >
                <Filter className="mr-2 h-3.5 w-3.5 text-stone-500" /> Filter
              </Button>
              <Button
                variant="outline"
                className="rounded-xl border-stone-200 text-xs text-stone-700"
              >
                <Download className="mr-2 h-3.5 w-3.5 text-stone-500" /> Export
                CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Data Records Table */}
        <Card className="shadow-xs overflow-hidden rounded-2xl border border-stone-200/80">
          <CardHeader className="border-b border-stone-100 bg-stone-50/80 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-display text-base font-semibold text-stone-800">
                {title} Records ({filteredItems.length})
              </CardTitle>
              <Badge
                variant="outline"
                className="border-amber-200 bg-amber-50 text-xs text-amber-800"
              >
                Active Module
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/40">
                  <TableHead className="w-[100px]">ID</TableHead>
                  <TableHead>Title / Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Date Modified</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map(item => (
                  <TableRow
                    key={item.id}
                    className="transition-colors hover:bg-amber-50/30"
                  >
                    <TableCell className="font-mono text-xs font-semibold text-amber-900">
                      {item.id}
                    </TableCell>
                    <TableCell className="font-medium text-stone-900">
                      {item.name}
                    </TableCell>
                    <TableCell className="text-xs text-stone-600">
                      {item.category}
                    </TableCell>
                    <TableCell className="text-xs text-stone-500">
                      {item.date}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={
                          item.status.includes("Published") ||
                          item.status.includes("Active") ||
                          item.status.includes("Indexed") ||
                          item.status.includes("Verified")
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border border-amber-200 bg-amber-50 text-amber-700"
                        }
                      >
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-stone-500 hover:text-amber-700"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-stone-500 hover:text-amber-700"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-lg text-stone-500 hover:text-rose-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

// Export individual page components
export const GalleryPage = () => <AdminPlaceholder title="Gallery" />;
export const BooksPage = () => <AdminPlaceholder title="Books (PDFs)" />;
export const ArticlesPage = () => (
  <AdminPlaceholder title="Articles/Tributes" />
);
export const BlogPage = () => <AdminPlaceholder title="Blog" />;
export const EventsPage = () => <AdminPlaceholder title="Events" />;
export const ScholarsPage = () => <AdminPlaceholder title="Scholars" />;
export const ResearchPage = () => <AdminPlaceholder title="Research Tools" />;
export const MuseumPage = () => <AdminPlaceholder title="Digital Museum" />;
export const AIPage = () => <AdminPlaceholder title="AI & Indexing" />;
export const AccessRequestsPage = () => (
  <AdminPlaceholder title="Access Requests" />
);
export const LogsPage = () => <AdminPlaceholder title="Logs" />;
export const SettingsPage = () => <AdminPlaceholder title="Settings" />;
